import React, { useState, useEffect, useCallback } from 'react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import apiFetch, { safeJson, apiErrorDetail } from '../../../context/api';

/**
 * Gestión de ubicaciones de stock (almacén / neveras / armarios) — Fase B.
 * Backend: GET/POST/DELETE /v1/stock/locations
 */
const KIND_LABELS = {
  principal: 'Principal',
  nevera: 'Nevera',
  armario: 'Armario',
  sub_almacen: 'Sub-almacén',
};

const KIND_BADGE = {
  principal: 'bg-indigo-50 text-indigo-700',
  nevera: 'bg-sky-50 text-sky-700',
  armario: 'bg-amber-50 text-amber-700',
  sub_almacen: 'bg-slate-100 text-slate-600',
};

// `canEdit` por defecto en false: si un día alguien abre este modal desde una
// pantalla nueva y se olvida del rol, sale en solo-lectura en vez de ofrecer
// botones que el backend rechaza con 403. Quien manda es el owner.
const StockLocationsModal = ({ token, venueID, onClose, canEdit = false }) => {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('nevera');
  const [saving, setSaving] = useState(false);
  const [pendienteBorrado, setPendienteBorrado] = useState(null);
  const [pendientePrincipal, setPendientePrincipal] = useState(null);
  // La principal de hoy: se nombra en el diálogo porque va a degradarse.
  const principalActual = locations.find((l) => l.kind === 'principal') || null;

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(venueID && { 'x-venue-id': venueID }),
  };

  const fetchLocations = useCallback(async () => {
    if (!venueID) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/v1/stock/locations?venue_id=${venueID}`, {
        headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) },
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cargar ubicaciones'));
      setLocations(data);
    } catch (e) {
      setError(e.message || 'Error al cargar ubicaciones');
    } finally {
      setLoading(false);
    }
  }, [venueID, token]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const createLocation = async () => {
    if (!name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch('/v1/stock/locations', {
        method: 'POST',
        headers,
        body: JSON.stringify({ venue_id: venueID, name: name.trim(), kind, is_sales_source: false }),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error al crear la ubicación'));
      setName('');
      setKind('nevera');
      await fetchLocations();
    } catch (e) {
      setError(e.message || 'Error al crear la ubicación');
    } finally {
      setSaving(false);
    }
  };

  // Se abre desde la misma pantalla de Productos, así que arrastraba el mismo problema:
  // en la tablet el `window.confirm` se traga y borrar una ubicación no hacía nada.
  const deleteLocation = (loc) => setPendienteBorrado(loc);

  // Marcar Almacén principal. Este modal no sabía hacer PATCH — hasta ahora solo
  // creaba y borraba. No mueve stock: cambia el destino por defecto del género nuevo
  // y saca la ubicación del pool de ventas de sala.
  const hacerPrincipal = async (loc) => {
    setError('');
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ kind: 'principal' }),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'No se pudo marcar como principal'));
      await fetchLocations();
    } catch (e) {
      setError(e.message || 'No se pudo marcar como principal');
    }
  };

  const borrarUbicacion = async (loc) => {
    setError('');
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) },
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error al borrar la ubicación'));
      await fetchLocations();
    } catch (e) {
      setError(e.message || 'Error al borrar la ubicación');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" {...overlayDismiss}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Cabecera */}
        <div className="flex items-start justify-between border-b border-[#E5E5E5] p-4">
          <div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">Ubicaciones de stock</h3>
            <p className="mt-0.5 text-sm text-[#737373]">Almacén, neveras y armarios de esta tienda</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[#737373] hover:bg-[#F5F5F5]" aria-label="Cerrar">✕</button>
        </div>

        <div className="p-4">
          {error && <p className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-[#DC2626]">{error}</p>}

          {/* Lista */}
          <div className="mb-4 max-h-64 overflow-y-auto">
            {loading && <p className="py-6 text-center text-sm text-[#737373]">Cargando…</p>}
            {!loading && locations.length === 0 && <p className="py-6 text-center text-sm text-[#737373]">Sin ubicaciones.</p>}
            {!loading && locations.length > 0 && (
              <ul className="divide-y divide-[#E5E5E5]">
                {locations.map((loc) => (
                  <li key={loc.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium text-[#0A0A0A]">{loc.name}</span>
                      <span className={`ml-2 inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_BADGE[loc.kind] || KIND_BADGE.sub_almacen}`}>
                        {KIND_LABELS[loc.kind] || loc.kind}
                      </span>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {/* Vetada para ventas = el backend rechaza `principal` con 400, así
                          que no se ofrece el botón (ver StockBoard, misma regla). */}
                      {canEdit && loc.kind !== 'principal' && (loc.dispensable ?? true) && (
                        <button onClick={() => setPendientePrincipal(loc)} className="rounded px-2 py-1 text-xs font-medium text-[#4F46E5] hover:bg-indigo-50">
                          Hacer principal
                        </button>
                      )}
                      {canEdit && loc.kind !== 'principal' && (
                        <button onClick={() => deleteLocation(loc)} className="rounded px-2 py-1 text-xs font-medium text-[#DC2626] hover:bg-red-50">
                          Borrar
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Crear — solo el dueño; al staff se le dice quién puede, no se le
              ofrece un botón que va a fallar. */}
          {!canEdit && (
            <p className="rounded-lg border border-[#E5E5E5] px-3 py-2.5 text-sm text-[#737373]">
              Las ubicaciones las gestiona el propietario del club.
            </p>
          )}
          {canEdit && (
          <div className="rounded-lg border border-[#E5E5E5] p-3 space-y-3">
            <p className="text-sm font-semibold text-[#0A0A0A]">Nueva ubicación</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="wa-form-field">
                <label className="wa-label">Nombre</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="wa-input" placeholder="Ej: Nevera barra" />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Tipo</label>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className="wa-input">
                  <option value="nevera">Nevera</option>
                  <option value="armario">Armario</option>
                  <option value="sub_almacen">Sub-almacén</option>
                </select>
              </div>
            </div>
            <button onClick={createLocation} disabled={saving} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
              {saving ? 'Creando…' : 'Crear ubicación'}
            </button>
          </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        active={!!pendientePrincipal}
        title={`¿Marcar «${pendientePrincipal?.name || ''}» como Almacén principal?`}
        description="El género nuevo entrará aquí por defecto. No se mueve nada de sitio. Las ventas dejarán de salir de aquí."
        // Este modal solo carga ubicaciones, no productos, así que no puede decir CUÁNTO
        // género hay dentro como sí hace el tablero de Ubicaciones. En vez de callarlo, se
        // avisa en genérico y se manda a la pantalla que sí lo sabe contar.
        note={[
          'Si esta ubicación tiene género dentro, dejará de venderse desde aquí: compruébalo en Almacén › Ubicaciones antes de confirmar.',
          principalActual && pendientePrincipal
            ? `«${principalActual.name}» pasará a Sub-almacén: dejará de ser el destino por defecto y volverá al pool de ventas.`
            : null,
        ].filter(Boolean).join(' ')}
        confirmLabel="Marcar como principal"
        onConfirm={() => {
          const loc = pendientePrincipal;
          setPendientePrincipal(null);
          if (loc) hacerPrincipal(loc);
        }}
        onCancel={() => setPendientePrincipal(null)}
      />

      <ConfirmDialog
        active={!!pendienteBorrado}
        title={`¿Borrar la ubicación «${pendienteBorrado?.name || ''}»?`}
        description="Su stock se devolverá al almacén principal."
        tone="danger"
        confirmLabel="Borrar ubicación"
        onConfirm={() => {
          const loc = pendienteBorrado;
          setPendienteBorrado(null);
          if (loc) borrarUbicacion(loc);
        }}
        onCancel={() => setPendienteBorrado(null)}
      />
    </div>
  );
};

export default StockLocationsModal;
